import Typography from '@mui/material/Typography';
import { Fade, Modal } from '@mui/material';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

import { useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import { FormTableAnalyticParameter } from 'components/ButtonComponent/ButtonTableAnalytic';
import { TableAnalyticComponent } from './TableAnalyticComponent';

const ModalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  maxWidth: 1200,
  bgcolor: 'background.paper',
  height: 'auto',
  maxHeight: '92%',
  borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15), 0px 4px 16px rgba(0, 0, 0, 0.08)',
};

export interface ModalTableAnalyticProps {
  open: boolean;
  onClose: () => void;
  subjectId?: string;
  parameterValue: FormTableAnalyticParameter;
}

export function ModalTableAnalytic(props: ModalTableAnalyticProps) {
  const { onClose, open, subjectId, parameterValue } = props;
  const { data: subjectDetails } = useGetSubjectDetails(subjectId);
  const handleOnClose = () => {
    onClose();
  };

  const displayName = subjectDetails?.data?.displayName;

  return (
    <Modal open={open} onClose={handleOnClose}>
      <Fade in={open} timeout={250}>
        <Stack direction='column' sx={ModalContentStyle}>
          {/* Header */}
          <Stack
            sx={{
              px: 3,
              py: 2,
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <Stack justifyContent='space-between' direction='row' alignItems='center'>
              <Stack direction='row' gap={1.5} alignItems='center'>
                <Stack
                  alignItems='center'
                  justifyContent='center'
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: '#ecfdf5',
                    flexShrink: 0,
                  }}
                >
                  <TroubleshootIcon sx={{ color: '#42BB93', fontSize: 20 }} />
                </Stack>
                <Stack>
                  <Typography
                    variant='body1'
                    component='h2'
                    fontWeight='bold'
                    sx={{ color: '#1D2939', fontSize: '16px', lineHeight: 1.3 }}
                  >
                    {displayName ? `${displayName} Analytics` : 'Table Analytics'}
                  </Typography>
                  <Typography sx={{ color: '#667085', fontSize: '12px' }}>
                    Pivot and visualize your data across different dimensions
                  </Typography>
                </Stack>
              </Stack>
              <IconButton
                onClick={handleOnClose}
                size='small'
                sx={{
                  color: '#98A2AE',
                  '&:hover': { color: '#667085', backgroundColor: '#f3f4f6' },
                }}
              >
                <HighlightOffRoundedIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Stack>
          </Stack>

          {/* Body */}
          <Stack sx={{ px: 3, py: 2, overflow: 'auto', flex: 1 }}>
            <TableAnalyticComponent parameterValue={parameterValue} subjectId={subjectId} />
          </Stack>
        </Stack>
      </Fade>
    </Modal>
  );
}
